/**
 * Sqreem ad click tracking
 * Parses sqclid from URL (e.g. ?sqclid=6qw487mcot1o), persists in sessionStorage,
 * and provides context for Segment events so sqclid is included on all events in the session.
 *
 * Uses sessionStorage (not localStorage) so that:
 * - sqclid persists across page navigations within the same tab
 * - sqclid is cleared when the tab/window is closed (correct for ad click attribution)
 */

const SQCLID_STORAGE_KEY = "hh_sqclid";

/**
 * Parse sqclid from current page URL and persist to sessionStorage if present.
 * Returns the current sqclid (from URL or sessionStorage).
 */
export const getSqclid = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = [...params.entries()]
      .find(([key]) => key.toLowerCase() === "sqclid")?.[1]
      ?.trim();
    if (fromUrl) {
      sessionStorage.setItem(SQCLID_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(SQCLID_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Returns Segment event properties for Sqreem: context_page_url and sqclid (if present).
 * Include in all Segment events so raw logs have context_page_url and sqclid for attribution.
 */
export const getSqreemEventProperties = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const sqclid = getSqclid();
    return {
      context_page_url: window.location.href,
      ...(sqclid && { sqclid }),
    };
  } catch {
    return {};
  }
};
